const { debounce, getElement, getElements } = window.BAO.Utils

class CollectionFiltersRenderer {
  /**
   * @param {CollectionFiltersForm} form
   */
  constructor (form) {
    /**
     * @type {CollectionFiltersForm}
     * @public
     */
    this.form = form
  }

  /**
   * @param {Function} filterDataUrl
   * @param {SubmitEvent} e
   *
   * @returns {void}
   */
  fromCache (filterDataUrl, e) {
    const html = this.form.filterData.find(element => filterDataUrl(element))
      .html

    this.form.log('fromCache', { filterDataUrl, e, html })

    this.render(html, e)
  }

  /**
   * @param {String} section
   * @param {String} url
   * @param {SubmitEvent} e
   *
   * @returns {void}
   */
  async fromFetch (section, url, e) {
    const html = await window.BAO.sectionRenderer.fetchSingle(section, url)

    this.form.log('fromFetch', { section, url, html })

    this.form.filterData.push({ html, url })

    this.render(html, e)
  }

  /**
   * @param {String} html
   * @param {SubmitEvent} e
   *
   * @returns {void}
   */
  render (html, e) {
    this.renderFilters(html, e)
    this.renderProductGridContainer(html)
    this.renderProductCount(html)
    this.setColumns()

    this.form.loading = false

    document.dispatchEvent(new CustomEvent('collectionRendered'))
  }

  /**
   * @param {String} html
   * @param {SubmitEvent} e
   *
   * @returns {void}
   */
  renderFilters (html, e) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html')
    const detailSelector = [
      this.form.els?.facet?.selector,
      this.form.externalEls?.facet?.selector,
    ]
      .filter(Boolean)
      .join(',')
    const facetDetailElements = getElements(detailSelector, {
      context: parsedHTML,
    })
    /**
     * @param {HTMLElement} element
     *
     * @returns {Boolean}
     */
    const matchesIndex = element => {
      const facet = e && e.target ? e.target.closest(detailSelector) : null

      return facet ? element.dataset.index === facet.dataset.index : false
    }
    const facetsToRender = facetDetailElements.filter(el => !matchesIndex(el))
    const countsToRender = facetDetailElements.find(el => matchesIndex(el))

    this.form.log('renderFilters', {
      e,
      parsedHTML,
      facetDetailElements,
      facetsToRender,
      countsToRender,
    })

    facetsToRender.forEach(element => {
      const selector = detailSelector
        .split(',')
        .map(selector => `${selector}[data-index="${element.dataset.index}"]`)
        .join(',')

      const el = getElement(selector)

      el.innerHTML = element.innerHTML
    })

    this.renderActiveFacets(parsedHTML)
    this.renderAdditionalElements(parsedHTML)

    if (countsToRender && e.target.closest(detailSelector)) {
      this.renderSelectedFacetCount(
        countsToRender,
        e.target.closest(detailSelector),
      )
    }
  }

  /**
   * @param {Document} parsedHTML
   *
   * @returns {void}
   */
  renderActiveFacets (parsedHTML) {
    if (!this.form.els['active-facets']?.element) return

    this.form.els['facets-nav'].element.innerHTML = getElement(
      this.form.els['facets-nav'].selector,
      { context: parsedHTML },
    ).innerHTML

    document.dispatchEvent(
      new CustomEvent('renderActiveFacets', {
        detail: {},
      }),
    )
  }

  /**
   * @param {Document} parsedHTML
   *
   * @returns {void}
   */
  renderAdditionalElements (parsedHTML) {
    this.form.externalEls['button-wrapper'].element.innerHTML = getElement(
      this.form.externalEls['button-wrapper'].selector,
      { context: parsedHTML },
    ).innerHTML

    this.form.els['title-bar'].element.innerHTML = getElement(
      this.form.els['title-bar'].selector,
      { context: parsedHTML },
    ).innerHTML

    this.form.els.reset.element.innerHTML = getElement(
      this.form.els.reset.selector,
      { context: parsedHTML },
    ).innerHTML

    this.form.els['facet-list'].element.innerHTML = getElement(
      this.form.els['facet-list'].selector,
      { context: parsedHTML },
    ).innerHTML

    // Re-init listeners.
    this.form.listeners.removeAll()
    this.form.setupListeners()
  }

  /**
   * @param {String} html
   *
   * @returns {void}
   */
  renderProductGridContainer (html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html')

    this.form.externalEls['product-grid'].element.innerHTML = getElement(
      this.form.externalEls['product-grid'].selector,
      { context: parsedHTML },
    ).innerHTML
  }

  /**
   * @param {String} html
   *
   * @returns {void}
   */
  renderProductCount (html) {
    const count = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(this.form?.els['product-count']?.selector)?.innerHTML

    this.form.log('renderProductCount', {
      html,
      count,
      countElements: this.countElements,
    })
    if (!count) return

    this.countElements.forEach(element => (element.innerHTML = count))
  }

  /**
   * @returns {void}
   */
  setColumns () {
    const gridSwitcherEl = getElement('[data-grid-switcher]')
    const cols = Number(gridSwitcherEl.getAttribute('data-columns'))

    // Reset the columns to re-trigger the grid switcher.
    if (cols > 0) {
      gridSwitcherEl.setAttribute('data-columns', cols)
    }
  }

  /**
   * @param {HTMLElement} source
   * @param {HTMLElement} target
   *
   * @returns {void}
   */
  renderSelectedFacetCount (source, target) {
    const sourceElement = getElement(
      this.form.els['facets-selected'].selector,
      { context: source },
    )
    const targetElement = getElement(
      this.form.els['facets-selected'].selector,
      { context: target },
    )

    if (sourceElement && targetElement) {
      targetElement.outerHTML = sourceElement.outerHTML
    }
  }

  /**
   * @returns {Element[]}
   */
  get countElements () {
    return getElements('[data-product-grid-count]')
  }

  /**
   * @returns {String[]}
   */
  get activeFacetSelectors () {
    if ('active-facets' in this.form.externalEls) {
      return [
        this.form.els['active-facets'].selector,
        this.form.externalEls['active-facets'].selector,
      ]
    }

    return [this.form.els['active-facets'].selector]
  }
}

/**
 * @typedef {Object} FilterData
 *
 * @property {string} html
 * @property {string} url
 */

export class CollectionFiltersForm extends window.BAO.CustomElement({
  SuperClass: HTMLFormElement,
}) {
  static get requiredElements () {
    return ['product-count', 'title-bar']
  }

  static get requiredExternalElements () {
    return ['button', 'button-wrapper']
  }

  constructor () {
    super()

    this.init = this.init.bind(this)
    this.reInit = this.reInit.bind(this)

    this.init()

    if (!window.BAO.MediaBreakpoints.isMd) {
      if (this.els.facet?.exists) {
        this.els.facet.element.removeAttribute('open')
      }
    }
  }

  init () {
    /**
     * @type {Section[]}
     * @public
     */
    this.sections = [
      {
        id: 'template-collection-product-grid',
        section: this.externalEls.container?.element.id,
        selector: '.shopify-section',
      },
    ]

    /**
     * @type {FilterData[]}
     * @public
     */
    this.filterData = []

    /**
     * @type {{ previous: URLSearchParams, current: URLSearchParams, initial: URLSearchParams }}
     * @public
     */
    this.searchParams = {
      // eslint-disable-next-line compat/compat
      previous: new URLSearchParams(window.location.search),
      // eslint-disable-next-line compat/compat
      current: new URLSearchParams(window.location.search),
      // eslint-disable-next-line compat/compat
      initial: new URLSearchParams(window.location.search),
    }

    /**
     * @type {CollectionFiltersRenderer}
     * @public
     */
    this.renderer = new CollectionFiltersRenderer(this)
  }

  reInit (e) {
    this.init()
    this.setupListeners()

    // eslint-disable-next-line compat/compat
    const searchParams = new URL(document.location).searchParams

    this.log('reInit', { searchParams })

    // Don't re-render unless there are filter values.
    if (searchParams.size > 0) {
      this.renderPage({ searchParams })
    }
  }

  setupListeners () {
    super.setupListeners()

    this.onFacetRemovalClick = this.onFacetRemovalClick.bind(this)
    this.onSubmit = this.onSubmit.bind(this)
    this.onResetClick = this.onResetClick.bind(this)
    this.onFacetKeyUp = this.onFacetKeyUp.bind(this)

    this.listeners.add(this, 'input', debounce(this.onSubmit, 500))
    this.listeners.add(window, 'popstate', this.onHistoryChange.bind(this))

    if (this.els.facets?.exists) {
      this.listeners.add(this.els.facets.element, 'keyup', this.onFacetKeyUp)
    }

    this.listeners.add(document, 'click', this.onPaginationClick.bind(this))

    if (this.externalEls.button.element.hasAttribute('data-reset-filters')) {
      this.listeners.add(
        this.externalEls.button.element,
        'click',
        this.onResetClick,
      )

      this.listeners.add(
        this.externalEls.button.element,
        'keyup',
        this.onResetClick,
      )
    }

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.COLLECTION.AFTER_SORT_BUTTON_CLICK,
      this.onSubmit,
    )

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.GRID_SWITCHER.SWITCHED,
      this.reInit,
    )
  }

  /**
   * @param {PopStateEvent} e
   *
   * @returns {void}
   */
  onHistoryChange (e) {
    const searchParams = e.state
      ? e.state.searchParams
      : this.form.searchParams.initial

    if (!searchParams) return

    if (
      searchParams.toString() === this.form.searchParams.previous.toString()
    ) {
      return
    }

    this.form.renderPage({ searchParams, updateURLHash: false })
  }

  /**
   * @param {MouseEvent} e
   *
   * @returns {void}
   */
  onResetClick (e) {
    // If event was a key press, ensure only ENTER key is used.
    if (e.type === 'keyup' && e.key !== 'Enter') return

    e.preventDefault()

    const searchParams = ''

    this.log('onResetClick', { searchParams })

    this.renderPage({ searchParams })
  }

  /**
   * @param {MouseEvent} e
   *
   * @returns {void}
   */
  onFacetRemovalClick (e) {
    e.preventDefault()

    const searchParams = e.currentTarget.href.includes('?')
      ? e.currentTarget.href.slice(e.currentTarget.href.indexOf('?') + 1)
      : ''

    this.log('onFacetRemovalClick', { searchParams })

    this.renderPage({ searchParams })
  }

  /**
   * @param {KeyboardEvent} e
   *
   * @returns {void}
   */
  onFacetKeyUp (e) {
    if (e.code.toUpperCase() === 'ESCAPE') {
      const openFacet = e.target.closest(`${this.els.facet.selector}[open]`)
      if (!openFacet) return

      const summary = getElement(`${this.els['facet-summary'].selector}`, {
        context: openFacet,
      })

      openFacet.removeAttribute('open')
      summary?.setAttribute('aria-expanded', 'false')
      summary?.focus()
    }
  }

  /**
   * @param {MouseEvent} e
   *
   * @returns {void}
   */
  onPaginationClick (e) {
    if (!e.target.closest(this.externalEls?.pagination?.selector)) return

    e.preventDefault()

    const anchor = e.target.closest('a')
    this.log('onPaginationClick', { anchor })
    if (!anchor || anchor.getAttribute('href') === '' || anchor.disabled) return

    const href = anchor.getAttribute('href')
    this.log('onPaginationClick', { href })
    // eslint-disable-next-line compat/compat
    const paginationSearchParams = new URLSearchParams(href.split('?')[1])
    const wantedPage = paginationSearchParams.get('page') ?? '1'
    // eslint-disable-next-line compat/compat
    const currentSearchParams = new URLSearchParams(
      this.searchParams.current.toString(),
    )
    currentSearchParams.set('page', wantedPage)

    this.renderPage({ searchParams: currentSearchParams.toString() })
  }

  /**
   * @param {InputEvent} e
   *
   * @returns {void}
   */
  onSubmit (e) {
    e.preventDefault()

    const formData = new FormData(this)
    // eslint-disable-next-line compat/compat
    const searchParams = new URLSearchParams(formData).toString()

    this.renderPage({ searchParams, e })
  }

  /**
   * @param {Object}
   * @param {String} searchParams
   * @param {InputEvent} [e]
   * @param {Boolean} [updateURLHash]
   *
   * @returns {void}
   */
  renderPage ({ searchParams, e, updateURLHash = true } = {}) {
    // Ensure the Availability filter is toggled on Size selection/de-selection
    if (typeof searchParams === 'object') searchParams = searchParams.toString()
    if (searchParams && searchParams.includes('filter.v.option.size')) {
      if (!searchParams.includes('filter.v.availability=1')) {
        searchParams += '&filter.v.availability=1'
      }
    } else {
      searchParams &&
        searchParams
          .replace('&filter.v.availability=1', '')
          .replace('filter.v.availability=1', '')
    }

    this.log('renderPage', { searchParams, e, updateURLHash })

    this.searchParams.previous = this.searchParams.current
    // eslint-disable-next-line compat/compat
    this.searchParams.current = new URLSearchParams(searchParams)

    this.loading = true

    this.sections.forEach(section => {
      const url = `${window.location.pathname}?section_id=${
        section.section
      }&${this.searchParams.current.toString()}`

      /**
       * @param {FilterData} element
       *
       * @returns {Boolean}
       */
      const filterDataUrl = element => element.url === url

      this.log('renderPageSections', { section, url })

      this.filterData.some(element => filterDataUrl(element))
        ? this.renderer.fromCache(filterDataUrl, e)
        : this.renderer.fromFetch(section.section, url, e)
    })

    if (updateURLHash) this.updateURLHash(this.searchParams.current)

    document.dispatchEvent(new CustomEvent('onFilterInteraction'))
  }

  /**
   * @param {URLSearchParams} searchParams
   *
   * @returns {void}
   */
  updateURLHash (searchParams) {
    history.pushState(
      { searchParams: searchParams.toString() },
      '',
      `${window.location.pathname}${searchParams &&
        '?'.concat(searchParams.toString())}`,
    )
  }

  /**
   * @returns {Boolean}
   */
  get loading () {
    return this._loading
  }

  /**
   * @param {Boolean} val
   *
   * @returns {void}
   */
  set loading (val) {
    this._loading = val

    this.setAttribute('aria-busy', val.toString())

    this.externalEls?.container?.element.setAttribute(
      'aria-busy',
      val.toString(),
    )
  }

  get isEditorialTemplateLoaded () {
    return getElement('[data-editorial-view-section]') !== null
  }
}

if (!customElements.get('collection-filters-form')) {
  customElements.define('collection-filters-form', CollectionFiltersForm, {
    extends: 'form',
  })
}

class PriceRange extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['min', 'max']
  }

  constructor () {
    super()

    this.setMinAndMaxValues()
  }

  setupListeners () {
    super.setupListeners()

    this.onRangeChange = this.onRangeChange.bind(this)

    getElements('input', { context: this }).forEach(el =>
      this.listeners.add(el, 'change', this.onRangeChange),
    )
  }

  /**
   * @param {InputEvent} e
   *
   * @returns {void}
   */
  onRangeChange (e) {
    this.adjustToValidValues(e.currentTarget)
    this.setMinAndMaxValues()
  }

  setMinAndMaxValues () {
    if (this.maxInput.value) {
      this.minInput.setAttribute('max', this.maxInput.value)
    }
    if (this.minInput.value) {
      this.maxInput.setAttribute('min', this.minInput.value)
    }
    if (this.minInput.value === '') this.maxInput.setAttribute('min', '0')
    if (this.maxInput.value === '') {
      this.minInput.setAttribute('max', this.maxInput.getAttribute('max'))
    }
  }

  /**
   * @param {HTMLInputElement} input
   *
   * @returns {void}
   */
  adjustToValidValues (input) {
    const value = Number(input.value)
    const min = Number(input.getAttribute('min'))
    const max = Number(input.getAttribute('max'))

    if (value < min) input.value = min.toString()
    if (value > max) input.value = max.toString()
  }

  /**
   * @returns {HTMLInputElement}
   */
  get maxInput () {
    return this.els.max.element
  }

  /**
   * @returns {HTMLInputElement}
   */
  get minInput () {
    return this.els.min.element
  }
}

if (!customElements.get('price-range')) {
  customElements.define('price-range', PriceRange)
}

class FacetRemove extends window.BAO.CustomElement() {
  setupListeners () {
    super.setupListeners()

    this.listeners.add(this.querySelector('a'), 'click', e => {
      e.preventDefault()

      this.form.onFacetRemovalClick(e)
    })
  }

  get form () {
    const selector = 'collection-filters-form, [is="collection-filters-form"]'

    return this.closest(selector) || getElement(selector)
  }
}

if (!customElements.get('facet-remove')) {
  customElements.define('facet-remove', FacetRemove)
}

class FilterFacet extends window.BAO.CustomElement({
  SuperClass: HTMLDetailsElement,
}) {
  static get requiredElements () {
    return ['summary', 'content']
  }

  static get requiredExternalElements () {
    return ['items', 'product-count']
  }

  constructor () {
    super()

    /**
     * @type {Animation|null}
     * @public
     */
    this.animation = null

    /**
     * @type {Boolean}
     * @public
     */
    this.isClosing = false

    /**
     * @type {Boolean}
     * @public
     */
    this.isExpanding = false
  }

  setupListeners () {
    super.setupListeners()

    this.onSummaryClick = this.onSummaryClick.bind(this)
    this.onLoad = this.onLoad.bind(this)
    this.onResize = this.onResize.bind(this)

    this.listeners.add(this, 'click', this.onSummaryClick)
    this.listeners.add(document, 'drawerOpen', debounce(this.onLoad, 200))
    this.listeners.add(window, 'resize', debounce(this.onResize, 200))
    this.listeners.add(
      document,
      'onFilterInteraction',
      debounce(this.onLoad, 200),
    )
  }

  onLoad () {
    this.setBodyHeight()
  }

  onResize () {
    this.setBodyHeight()
  }

  /**
   * @param {MouseEvent} e
   *
   * @returns {void}
   */
  onSummaryClick (e) {
    if (!e.target.closest(this.els.summary.selector)) return

    e.preventDefault()

    // Close all other open facets
    const facets = getElements('[is="filter-facet"]')

    facets.forEach(facet => {
      if (facet !== this && facet.open) {
        facet.close()
      }
    })

    if (this.isClosing || !this.open) {
      this.start()
    } else if (this.isExpanding || this.open) {
      this.close()
    }

    this.setBodyHeight()
  }

  setBodyHeight () {
    if (window.innerWidth < 768) {
      this.els.body.element.style.height = ''
    } else {
      const bodyHeight = Math.abs(
        this.externalEls['product-count'].element.offsetTop -
          this.externalEls.items.element.offsetTop -
          20,
      )

      if (!this.els.body?.exists) return

      // Set a height on the content body so it can be scrolled.
      // This is required because the content body is absolutely
      // positioned so has no concept of height.
      this.els.body.elements.forEach(el => {
        el.style.height = `${bodyHeight}px`
      })

      window.setTimeout(() => {
        this.els.body.elements.forEach(el => {
          el.style.height = `${bodyHeight}px`
        })
      }, 500)
    }
  }

  start () {
    this.open = true

    window.requestAnimationFrame(() => this.expand())
  }

  close () {
    this.shrink()
  }

  shrink () {
    this.isClosing = true

    if (this.animation) {
      this.animation.cancel()
    }

    this.animation = this.content.animate(
      {
        opacity: [1, 0],
      },
      { duration: 200, easing: 'ease' },
    )

    this.animation.onfinish = () => this.onAnimationFinish(false)
    this.animation.oncancel = () => (this.isClosing = false)
  }

  expand () {
    this.isExpanding = true

    if (this.animation) {
      this.animation.cancel()
    }

    this.animation = this.content.animate(
      {
        opacity: [0, 1],
      },
      { duration: 200, easing: 'ease' },
    )

    this.animation.onfinish = () => this.onAnimationFinish(true)
    this.animation.oncancel = () => (this.isExpanding = false)
  }

  isOverflowY (element) {
    return (
      element.scrollHeight !==
      Math.max(element.offsetHeight, element.clientHeight)
    )
  }

  /**
   * @param {Boolean} open
   *
   * @returns {void}
   */
  onAnimationFinish (open) {
    this.open = open
    this.animation = null

    this.isClosing = false
    this.isExpanding = false
  }

  get content () {
    return window.BAO.Utils.getElement(this.els.content.selector, {
      context: this,
    })
  }
}

if (!customElements.get('filter-facet')) {
  customElements.define('filter-facet', FilterFacet, { extends: 'details' })
}
class SortButtons extends window.BAO.CustomElement() {
  setupListeners () {
    super.setupListeners()

    if (!this.externalEls?.select?.element) return

    this.listeners.add(document, 'click', this.onSortButtonClick.bind(this))
  }

  /**
   * @param {MouseEvent} e
   *
   * @returns {void}
   */
  onSortButtonClick (e) {
    if (!e.target.closest(this.els?.trigger?.selector)) return

    const triggerEl = e.target.closest(this.els?.trigger?.selector)

    this.externalEls.select.element.value = triggerEl.dataset.value

    window.BAO.dispatchEvent(
      window.BAO.EVENTS.COLLECTION.AFTER_SORT_BUTTON_CLICK,
      {},
    )
  }
}

if (!customElements.get('sort-buttons')) {
  customElements.define('sort-buttons', SortButtons)
}
